import { Component, EventEmitter, Input, Output, OnDestroy, OnInit } from '@angular/core';
import { Subject, Subscription, of } from 'rxjs';
import { debounceTime, switchMap, tap } from 'rxjs/operators';
import { FieldValidationState } from '../../../features/users/users.model';
import { UsersService } from '../../../features/users/users.service';

@Component({
  selector: 'app-email-input',
  standalone: false,
  templateUrl: './email-input.component.html',
  styleUrl: './email-input.component.scss'
})
export class EmailInputComponent implements OnInit, OnDestroy {
  @Input() value: string = '';
  @Input() validationState: FieldValidationState = { status: 'idle' };
  @Input() originalValue?: string;
  @Output() valueChange = new EventEmitter<string>();
  @Output() validationStateChange = new EventEmitter<FieldValidationState>();

  private inputSubject = new Subject<string>();
  private sub?: Subscription;

  constructor(private usersService: UsersService) { }

  ngOnInit(): void {
    this.sub = this.inputSubject.pipe(
      debounceTime(500),
      switchMap((val) => {
        if (!val) {
          this.setValidationState({ status: 'idle' });
          return of(null);
        }
        if (this.originalValue !== undefined && val === this.originalValue) {
          this.setValidationState({ status: 'valid' });
          return of(null);
        }
        if (!this.isValidEmail(val)) {
          this.setValidationState({ status: 'error', message: 'This email format is invalid' });
          return of(null);
        }
        // Valid email format, perform async check
        this.setValidationState({ status: 'checking' });
        return this.usersService.checkEmailUniqueness(val);
      })
    ).subscribe({
      next: (res) => {

        if (!res) return;
        if (res.available) {
          this.setValidationState({ status: 'valid', message: '✓ Available' });
        } else {
          this.setValidationState({
            status: 'conflict',
            conflictUser: res.user || { full_name: 'Sara Ridouani', id: 'USR-04829' },
            message: `This email is already in use by ${res.user?.full_name || 'Sara Ridouani'} (${res.user?.id || 'USR-04829'})`
          });
        }
      },
      error: (err) => {
        this.setValidationState({ status: 'error', message: 'Error checking email uniqueness' });
      }
    });
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  onInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    const val = target.value;
    this.value = val;
    this.valueChange.emit(val);

    if (!val) {
      this.setValidationState({ status: 'idle' });
    } else if (this.originalValue !== undefined && val === this.originalValue) {
      this.setValidationState({ status: 'valid' });
    } else if (!this.isValidEmail(val)) {
      this.setValidationState({ status: 'error', message: 'This email format is invalid' });
    } else {
      this.setValidationState({ status: 'checking' });
      this.inputSubject.next(val);
    }
  }

  private setValidationState(state: FieldValidationState): void {
    this.validationState = state;
    this.validationStateChange.emit(state);
  }

  private isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  onOpenExistingUser(event: Event): void {
    event.preventDefault();
    // console.log('Open existing user clicked for user ID:', this.validationState.conflictUser?.id);
  }
}
